import { useCallback } from 'react';
import type { Graph } from '@antv/x6';
import { calculateStatePositions, calculateObjectSize, calculateNaturalStateWidths, calculateMinParentWidthForNaturalStates } from '../utils/sizing';

export function useGraphData(
  graphRef: React.MutableRefObject<Graph | null>
) {
  const updateObjectLabelAnchor = (node: any, hasStates: boolean) => {
    if (node?.getData()?.type !== 'object') return;
    node.attr('label/refY', hasStates ? 26 : '55%');
    node.attr('label/textVerticalAnchor', hasStates ? 'top' : 'middle');
  };

  const loadNodes = useCallback((nodes: any[]) => {
    const graph = graphRef.current;
    if (!graph) return;

    // 添加所有节点
    nodes.forEach((nodeData) => {
      graph.addNode(nodeData);
    });

    // 建立父子关系（将状态/值节点嵌入对象节点）
    nodes.forEach((nodeData) => {
      const data = nodeData.data;
      if (data.parentId) {
        const parentNode = graph.getCellById(data.parentId);
        const childNode = graph.getCellById(nodeData.id);
        if (parentNode?.isNode() && childNode?.isNode()) {
          parentNode.embed(childNode);
        }
      }
    });

    // 规范化布局：根据数据重新计算状态节点位置和父节点尺寸
    graph.getNodes().forEach((n: any) => {
      const children = n.getChildren() || [];
      const stateChildren = children.filter((child: any) => child.getData()?.type === 'state');

      if (stateChildren.length > 0) {
        const parentPos = n.getPosition();
        const stateLabels = stateChildren.map((child: any) => child.getData()?.label || '');
        const naturalWidths = calculateNaturalStateWidths(stateLabels);
        const minStatesWidth = calculateMinParentWidthForNaturalStates(naturalWidths);
        const labelSize = calculateObjectSize(n.getData()?.label || '', true);
        const parentWidth = Math.max(minStatesWidth, labelSize.width);

        // 调整父节点宽度以适应状态节点的自然宽度
        n.setSize({ width: parentWidth, height: n.getSize().height });

        const positions = calculateStatePositions(parentPos, parentWidth, stateChildren.length, naturalWidths);

        stateChildren.forEach((child: any, i: number) => {
          const pos = positions[i];
          child.setPosition({ x: pos.x, y: pos.y });
          child.setSize({ width: pos.width, height: child.getSize().height });
        });

        // 根据子节点的实际边界重新计算父节点尺寸
        const childBounds = stateChildren.map((child: any) => ({
          x: child.getPosition().x - parentPos.x,
          y: child.getPosition().y - parentPos.y,
          width: child.getSize().width,
          height: child.getSize().height,
        }));
        const newSize = calculateObjectSize(n.getData().label, true, childBounds);
        n.setSize({ width: parentWidth, height: newSize.height });
      }

      n.setData({ childStateCount: stateChildren.length });
      updateObjectLabelAnchor(n, stateChildren.length > 0);
    });
  }, [graphRef]);

  return { loadNodes };
}
